import { useMutation, useQuery } from '@apollo/client';
import { faArchive, faPaperPlane } from '@fortawesome/pro-light-svg-icons';
import { faArrowLeft, faPaperclip } from '@fortawesome/pro-regular-svg-icons';
import {
  Button,
  Card,
  IconButton,
  Input,
  LegalBody,
  MediumTitle,
  Multiselect,
  Select,
  SmallBody,
  SmallTitle,
  Textarea,
  palettes,
  theme
} from '@indigo-ag/component-module';
import {
  AgConversation,
  AgFinancingDocumentType,
  AgFinancingFileFolder,
  AgFinancingFilesContext,
  FinancingRole
} from '@indigo-ag/schema';
import { Logger } from '@indigo-ag/ui-core';
import i18next from 'core/i18n';
import { Dictionary } from 'lodash';
import React, { memo, useContext, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { UserContext } from 'core/components/GateWay/GateWay';
import { allConversationAvailableBrRoles } from 'features/admin/adminQueries';
import { FinanceProcessContext } from 'features/brazil-grower/pages/BrazilGrowersDetail';
import {
  createAgConversation,
  getConversationsMessagesForFinancing,
  sendAgMessage
} from 'features/brazil-grower/queries';
import {
  AttachButton,
  AttachmentFile,
  DocumentCard,
  LoadingIndicator,
  UploadDocumentCard,
  documentRequestTypesOptions,
  documentRequestTypesTranslations
} from 'shared';
import { createStylesHook } from 'shared/hooks/createStylesHook';
import { UserUtil, date, usePrompt } from 'shared/utils';
import { ProgressDictionary, uploadFilesToAgFinancing } from '../../BrazilGrowerFinance/utils';
import { TitleWrap } from '../TitleWrap/TitleWrap';

const StyledTextArea = styled(Textarea)`
  margin: 0px 12px;
  width: 100%;
`;

const StyledMultiSelect = styled(Multiselect)`
  width: 100%;
  margin-bottom: 0px;
  margin-right: 10px;
`;

const StyledSelect = styled(Select)`
  margin-bottom: 12px;
  margin-right: 20px;
  width: 180px;
`;

const StyledInput = styled(Input)`
  margin-bottom: 0px;
  margin-right: 10px;
  min-width: 250px;
`;

const useStyles = createStylesHook({
  chatBox: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%'
  },
  chatHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 15px 0 20px',
    marginBottom: 2,
    boxShadow: '0px 1px 1px rgba(0, 87, 125, 0.1), 0px 1px 2px rgba(0, 87, 125, 0.14);',
    backgroundColor: palettes.brand.gray['100']
  },
  chatContent: {
    flex: 1,
    padding: 20,
    overflowY: 'auto'
  },
  message: {
    display: 'flex',
    flexDirection: 'column',
    padding: '10px 20px',
    width: '90%',
    marginBottom: 20,
    '& .sent-by': {
      display: 'flex',
      marginTop: 10
    },
    '& .sent-at': {
      display: 'flex',
      justifyContent: 'flex-end',
      color: palettes.brand.gray['700']
    },
    '&.own': {
      marginLeft: 'auto',
      backgroundColor: palettes.brand.teal.lightest,
      '& .sent-by': {
        display: 'none'
      }
    }
  },
  chatActions: {
    alignItems: 'flex-end',
    padding: 12,
    backgroundColor: palettes.brand.gray['100']
  },
  attachments: {
    padding: '20px 0',
    '& .attachment-row': {
      maxWidth: 350,
      paddingBottom: 4,
      '& .document-type': {
        color: palettes.brand.gray['700']
      }
    }
  },
  newAttachment: {
    padding: '0px 76px',
    backgroundColor: palettes.brand.gray['100'],
    maxHeight: '40vh',
    overflowY: 'auto',
    [theme.breakpoints.up('xl')]: {
      maxHeight: 500
    },
    '& .attachment-header': {
      padding: '24px 0'
    },
    '& .attachment-row': {
      width: '100%',
      paddingBottom: 15,
      display: 'flex',
      alignItems: 'center',
      '& .attachment-file': {
        minWidth: 200,
        marginTop: 12
      },
      [theme.breakpoints.down('md')]: {
        alignItems: 'flex-start',
        flexDirection: 'column'
      }
    }
  },
  messageText: {
    whiteSpace: 'break-spaces'
  }
});

export interface ChatBoxProps {
  openNewConversation: boolean;
  onCreateNewConversation: (conversation: AgConversation) => void;
  selectedConversation: AgConversation | undefined;
  isArchiveView: boolean;
  onArchive: (conversation: Partial<AgConversation>) => void;
}

export const ChatBox: React.FunctionComponent<ChatBoxProps> = memo(
  ({ openNewConversation, onCreateNewConversation, selectedConversation, isArchiveView, onArchive }) => {
    /**
     * State
     */
    const [documentTypeDictionary, setUpdateDocumentType] = useState<Dictionary<AgFinancingDocumentType>>({});
    const [selectedFiles, setSelectedFiles] = useState<AttachmentFile[]>([]);
    const [messageBody, setMessageBody] = useState('');
    const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
    const [subject, setSubject] = useState<string>('');
    const [progress, setProgress] = useState<ProgressDictionary>();
    const [messageLoading, setMessageLoading] = useState(false);
    const chatMessagesEl = useRef<HTMLDivElement>(null);
    const [isWritingMessage, setIsWritingMessage] = useState(false);
    const [warningActive, setWarningActive] = useState(false);
    /**
     * Mutations
     */
    const [createAgConversationFn] = useMutation(createAgConversation);
    const [sendAgMessageFn] = useMutation(sendAgMessage);

    /**
     * Finance Process Context
     */
    const { agFinancingId } = useContext(FinanceProcessContext);
    const { userId, role: currentUserRole } = useContext(UserContext);

    /**
     * Queries
     */
    const { data: rolesQueryResult } = useQuery<{ allConversationAvailableBrRoles: FinancingRole[] }>(
      allConversationAvailableBrRoles
    );
    const roles = rolesQueryResult?.allConversationAvailableBrRoles || [];
    const { data: agConversationQueryResult, loading: loadingMessages, error, stopPolling } = useQuery<{
      financingAgFinancingConversations: AgConversation[];
    }>(getConversationsMessagesForFinancing, {
      variables: { agFinancingId, conversationId: selectedConversation?.id },
      fetchPolicy: 'network-only',
      pollInterval: 10000
    });
    if (error) {
      Logger.warn('Encountered error during getConversationsMessagesForFinancing polling. Stopping poll requests.');
      stopPolling();
    }
    const messages = agConversationQueryResult?.financingAgFinancingConversations[0]?.messages || [];

    /**
     * Styles
     */
    const classes = useStyles();

    /**
     * Callbacks
     */
    const handleMessageBodyChange = ({ target: { value } }: React.ChangeEvent<HTMLTextAreaElement>) => {
      setMessageBody(value);
      setIsWritingMessage(value.length ? true : false);
    };

    const handleFilesSelected = (files: AttachmentFile[]) => {
      setSelectedFiles(files);
      setIsWritingMessage(true);
    };

    const handleFileRemove = (fileId: string) => {
      setSelectedFiles(selectedFiles.filter(f => f['newFileId'] !== fileId));
    };

    const handleUpdateDocumentType = (fileId: string) => {
      return (newDocumentType: string | null) => {
        if (newDocumentType) {
          setUpdateDocumentType({ ...documentTypeDictionary, [fileId]: newDocumentType as AgFinancingDocumentType });
        }
      };
    };

    const handleRoleSelection = (options: string[]) => {
      setSelectedRoles(options);
    };

    const handleSubjectChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      setSubject(event.target.value);
    };

    const handleSendMessage = () => {
      setMessageLoading(true);
      setWarningActive(false);
      const preWork = selectedFiles.length
        ? uploadFilesToAgFinancingFn(
            selectedFiles,
            AgFinancingFilesContext.DOCUMENTS,
            documentTypeDictionary,
            percentCompleted => setProgress({ ...percentCompleted })
          ).then(uploadedFiles => {
            return {
              body: messageBody,
              attachments: uploadedFiles.fulfilled.map(({ documentName, documentUrl, documentType }) => ({
                documentName,
                documentUrl,
                documentType
              }))
            };
          })
        : Promise.resolve({
            body: messageBody,
            attachments: []
          });

      const postWork = (conversation?: AgConversation) => {
        setIsWritingMessage(false);
        setMessageBody('');
        setSelectedFiles([]);
        setMessageLoading(false);
        if (openNewConversation && conversation) {
          onCreateNewConversation(conversation);
        }
      };

      if (openNewConversation) {
        preWork
          .then(message =>
            createAgConversationFn({
              awaitRefetchQueries: true,
              variables: {
                agFinancingId,
                roles: selectedRoles,
                subject: subject.length ? subject : undefined,
                message
              },
              refetchQueries: ['financingAgFinancingConversations', 'financingAdminNotifications']
            })
          )
          .then(({ data }: { data?: { createAgConversation: AgConversation } }) => data?.createAgConversation)
          .then(postWork);
      } else {
        preWork
          .then(message =>
            sendAgMessageFn({
              awaitRefetchQueries: true,
              variables: {
                conversationId: selectedConversation?.id,
                message
              },
              refetchQueries: [
                {
                  query: getConversationsMessagesForFinancing,
                  variables: { agFinancingId, conversationId: selectedConversation?.id }
                }
              ]
            })
          )
          .then(({ data }: { data?: { createAgConversation: AgConversation } }) => data?.createAgConversation)
          .then(postWork);
      }
    };

    const handleArchiveConversation = () => {
      const conversation: Partial<AgConversation> = {
        id: selectedConversation?.id,
        archived: !isArchiveView
      };

      onArchive(conversation);
    };

    /**
     * Variables
     */
    const allFilesWithType = !selectedFiles.some(f => !documentTypeDictionary[f.newFileId]);
    const isSendDisabled = openNewConversation
      ? !selectedRoles.length || (selectedFiles.length ? !allFilesWithType : !messageBody.length)
      : selectedFiles.length
      ? !allFilesWithType
      : !messageBody.length;
    const uploadFilesToAgFinancingFn = uploadFilesToAgFinancing({
      agFinancingId,
      folder: AgFinancingFileFolder.CONVERSATIONS
    });

    useEffect(() => {
      if (messages && messages.length) {
        if (!!chatMessagesEl.current) {
          chatMessagesEl.current.scrollTop = chatMessagesEl.current.scrollHeight;
        }
      }
    }, [messages]);

    useEffect(() => {
      if ((selectedConversation && selectedConversation.id) || openNewConversation) {
        setMessageBody('');
        setSelectedFiles([]);
        setSelectedRoles([]);
        setSubject('');
      }
    }, [selectedConversation, openNewConversation]);

    useEffect(() => {
      setWarningActive(false);
    }, [selectedConversation]);

    const todayAt00 = date.todayAt00();
    usePrompt(isWritingMessage);

    const activateWarning = () => {
      setWarningActive(true);
    };

    return (
      <Card className={classes.chatBox}>
        <div className={classes.chatHeader}>
          {openNewConversation ? (
            <div className="flex flex1 mb-3 mt-3">
              <StyledMultiSelect
                name="role-names"
                onSelectionChanged={handleRoleSelection}
                options={roles
                  .filter(r => r.id !== currentUserRole.id)
                  .map(r => ({ value: r.id, name: i18next.t(r.id) }))}
                value={selectedRoles}
                label={`${i18next.t('conversationFor')}: `}
                placeholder={i18next.t('pleaseSelect')}
                error={!selectedRoles.length}
              />
              <StyledInput
                label={`${i18next.t('subject')} (${i18next.t('optional')})`}
                value={subject}
                onChange={handleSubjectChange}
              />
            </div>
          ) : (
            <>
              <TitleWrap roles={selectedConversation?.roles} subject={selectedConversation?.subject} extraSize={true} />
              <IconButton
                icon={isArchiveView ? faArrowLeft : faArchive}
                onClick={handleArchiveConversation}
                style={{ minWidth: 40 }}
              />
            </>
          )}
        </div>
        <div className={classes.chatContent} ref={chatMessagesEl}>
          {selectedConversation ? (
            loadingMessages ? (
              <LoadingIndicator />
            ) : (
              <>
                {messages.map(message => (
                  <Card className={`${classes.message} ${message.sentBy.id === userId ? 'own' : ''}`} key={message.id}>
                    <SmallTitle className="sent-by">{`${UserUtil.getUserFullName(message.sentBy)} - ${i18next.t(
                      message.sentBy.role.id
                    )}`}</SmallTitle>
                    {message.body && <SmallBody className="break-line">{message.body}</SmallBody>}
                    {message.attachments && message.attachments.length ? (
                      <div className={classes.attachments}>
                        {message.attachments.map(attachment => (
                          <div className="attachment-row" key={attachment.id}>
                            <LegalBody className="document-type text-transform-capitalize">
                              {documentRequestTypesTranslations[attachment.documentType]}
                            </LegalBody>
                            <DocumentCard document={attachment} />
                          </div>
                        ))}
                      </div>
                    ) : null}
                    <LegalBody className="sent-at">{date.messageSentAtFormat(message.sentAt, todayAt00)}</LegalBody>
                  </Card>
                ))}
              </>
            )
          ) : null}
        </div>
        <div>
          {!!selectedFiles.length && (
            <div className={classes.newAttachment}>
              <MediumTitle className="attachment-header">{i18next.t('conversationAttachFiles')}</MediumTitle>
              {selectedFiles.map(file => (
                <div className="attachment-row" key={file['newFileId']}>
                  <StyledSelect
                    key={file['newFileId']}
                    sizeVariant="slim"
                    label={i18next.t('documentType')}
                    name="documentType"
                    options={documentRequestTypesOptions}
                    value={documentTypeDictionary[file['newFileId']]}
                    onSelectionChanged={handleUpdateDocumentType(file['newFileId'])}
                    placeholder={i18next.t('pleaseSelect')}
                    error={!documentTypeDictionary[file['newFileId']]}
                    helpText={!documentTypeDictionary[file['newFileId']] ? i18next.t('pleaseSelect') : ''}
                  />
                  <div className="attachment-file">
                    <UploadDocumentCard
                      documentName={file.name}
                      progress={progress ? progress[file['newFileId']] : undefined}
                      onRemove={() => handleFileRemove(file['newFileId'])}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
          {(!isArchiveView || openNewConversation) && (
            <div className={`flex ${classes.chatActions}`}>
              <AttachButton faIcon={faPaperclip} variant="tertiary" onFilesSelected={handleFilesSelected} />
              <StyledTextArea
                label=""
                placeholder={i18next.t('conversationMessagePlaceholder')}
                value={messageBody}
                onChange={handleMessageBodyChange}
                error={openNewConversation || warningActive ? !messageBody : false}
                onClick={activateWarning}
              />
              <Button
                faIcon={faPaperPlane}
                onClick={handleSendMessage}
                disabled={isSendDisabled}
                busy={messageLoading}
              />
            </div>
          )}
        </div>
      </Card>
    );
  }
);
